import * as React from 'react';
import { css } from '@emotion/core';
import { TABLE_COLORS, COLORS } from '../../globalStyle/colors';
import LocalDiv from '../../localization/LocalDiv';
import { Link } from 'react-router-dom';
import { useMsgText } from '../../localization/hooks';
import { Str } from '../../localization/translationKeys';
import Msg from '../../localization/Msg';
import { Report } from './models';
import { btnStyle } from '../../forms/styles/button';
import { pathCombine } from '../../core/api/module';
import { BaseUrl } from '../../core/api/endpoints';
import LocalBtn from '../../localization/LocalBtn';
import { FLEX_ADAPTIVE_MED } from '../../layout/flexBox';
import Spacer from '../../layout/Spacer';
import { isNilOrEmpty } from '../../language/module';
import { useAppState } from '../../core/redux/hooks';


const styles = {
  root:     css`
    background-color: ${TABLE_COLORS.DARK_BG};
    padding: 25px 25px 15px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
  `,
  dataRow:  css`
    display: flex;
    flex-direction: row;
    margin-bottom: 5px;
  `,
  btnRow:   {
    root: [
      FLEX_ADAPTIVE_MED,
      css`
        align-items: flex-start;
      `,
    ],
    btn:  css`
      min-width: 192px;
    `,
  },
  label:    css`
    font-weight: bold;
  `,
  link:     css`
    display: block;
    width: 180px;
  `,
  disabled: css`
    cursor: default;
    opacity: .6;

    &:hover {
      text-decoration: none;
      cursor: default;
    }
  `,
  courseCompletionMsg: css`
    color: ${COLORS.GREEN};
  `,  
};


type Props = {
  report: Report;
  language: string;

  showShareScoresBtn: boolean;
  showDownloadReportBtn: boolean;
  showStatusColumn: boolean;
  courseCompletionMsg: string;
  courseAllowViewReport: boolean;  

  setReport: () => void;
  onClickShareScoresBtn: () => void;

  className?: string;
};


/**
 * ReportCard: a component that renders a summary for a report, including a link to view that report.
 * Mostly used on mobile.
 *
 * PureComponent: true
 * Redux Connected: false
 */
const ReportCard: React.FC<Props> = props => {
  const btnText  = useMsgText(props.showDownloadReportBtn && !props.report.webReport ? Str.Download : Str.ViewReport);

  const downloadURL = (report: Report) => {
    const baseUrl = pathCombine(BaseUrl, '/report/reportDownload');
    return report?.fileExists ? baseUrl + '/' + encodeURIComponent(report!.reportFile) : undefined;
  };

  const reportType = (report: Report) => (
    report.reportTypeMessageKey !== 'Portal-AggregateReport'
    ? <Msg str={report.reportTypeMessageKey} />
    : isNilOrEmpty(report.groupReportTitle)
      ? <Msg str={report.reportTypeMessageKey} />
      : <span>{report.groupReportTitle}</span>
  );

  const eCourseReport = (report: Report) => (
    report.reportTypeMessageKey === 'Portal-SelfReport'
  );

  const canView = (report: Report, courseAllowViewReport: boolean) => (
    eCourseReport(report) || (!eCourseReport(report) && (!report.checkCourseCompletionPct || courseAllowViewReport))
  );

  const statusText = (report: Report, courseCompletionMsg: string, courseAllowViewReport: boolean) => (
    canView(report, courseAllowViewReport)
    ? <Msg str={Str.Available} />
    : <span css={styles.courseCompletionMsg}>{courseCompletionMsg}</span>
  );
    
  const code = useAppState(state => state.locale.lang.code);
  
  return (
    <LocalDiv css={styles.root} className={props.className}>
      <LocalDiv css={styles.dataRow}>
        <Msg str={Str.Reports} css={styles.label} />
        <span>:</span>
        <span>&nbsp;</span>
        {reportType(props.report)}
      </LocalDiv>
      <LocalDiv css={styles.dataRow}>
        <Msg str={Str.Delivered} css={styles.label} />
        <span>:</span>
        <span>&nbsp;</span>
        <span>{new Date(props.report.effectiveDate).toLocaleDateString(code)}</span>
      </LocalDiv>
      <LocalDiv css={styles.dataRow}>
        <Msg str={Str.Language} css={styles.label} />
        <span>:</span>
        <span>&nbsp;</span>
        <span>{props.language}</span>
      </LocalDiv>
      {
        props.showStatusColumn && (
          <LocalDiv css={styles.dataRow}>
            <Msg str={Str.Status} css={styles.label} />
            <span>:</span>
            <span>&nbsp;</span>
            <span>{
              statusText(
                props.report,
                props.courseCompletionMsg,
                props.courseAllowViewReport,
            )}</span>
          </LocalDiv>
        )
      }
      <Spacer sizePx={25} />
      <LocalDiv css={styles.btnRow.root}>
        {
          props.showDownloadReportBtn
          && !props.report.webReport
          && canView(props.report, props.courseAllowViewReport)
            ? (
              <a
                href={downloadURL(props.report)}
                css={[
                  btnStyle.primary(COLORS.BLUE),
                  styles.link,
                  styles.btnRow.btn,
                  (
                    !props.report.fileExists 
                    || !canView(props.report, props.courseAllowViewReport)
                  ) && styles.disabled,
                ]}
                target={'_blank'}
                rel='noopener noreferrer'
              >
                {btnText}
              </a>
            ) : (
              <Link
                to='#'
                css={[btnStyle.primary(COLORS.BLUE), styles.link, styles.btnRow.btn]}
                onClick={canView(props.report, props.courseAllowViewReport)
                  ? props.setReport 
                  : undefined
                }
              >
                {btnText}
              </Link>
            )
        }
        {
          props.showShareScoresBtn && (
            <>
              <Spacer sizePx={8} />
              <LocalBtn
                str={Str.ShareScores}
                css={[btnStyle.transparent(COLORS.DARK_GREY), styles.btnRow.btn]}
                onClick={props.onClickShareScoresBtn}
              />
            </>
          )
        }
      </LocalDiv>
    </LocalDiv>
  );
};

export default ReportCard;
